import { authService } from '@/services/auth-api'
import router from '@/router'
import { setRemember, writeToken, clearToken, clearSession, readToken, ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/utils/token-storage'
import { acceptMeIdentity, acceptRefreshedToken, bootSessionVerdict } from '@/utils/session-hygiene'
import { navigateAfterLogin } from '@/utils/post-login-redirect'
import { changePasswordErrorMessage } from '@/utils/password-change'

const state = {
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  tokenValidSeconds: null,
  loginAttempts: 0,
  lastLoginAttempt: null
}

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  currentUser: state => state.user,
  accessToken: state => state.accessToken,
  refreshToken: state => state.refreshToken,
  canAttemptLogin: state => {
    // Rate limiting: max 5 attempts per 15 minutes
    if (state.loginAttempts >= 5) {
      const timeSinceLastAttempt = Date.now() - state.lastLoginAttempt
      return timeSinceLastAttempt > 15 * 60 * 1000 // 15 minutes
    }
    return true
  }
}

const mutations = {
  SET_AUTH_DATA(state, { accessToken, refreshToken, tokenValidSeconds, user }) {
    state.accessToken = accessToken
    state.refreshToken = refreshToken
    state.tokenValidSeconds = tokenValidSeconds
    state.user = user
    state.isAuthenticated = true

    // writeToken() honours the cs:remember flag: localStorage when checked,
    // sessionStorage otherwise. The login action clears the previous session
    // and sets the flag before this runs.
    //
    // An ABSENT value clears the stored one rather than leaving it. The old
    // `if (refreshToken)` guard meant a response without a refresh token left
    // the PREVIOUS identity's refresh token in storage, where the 401
    // interceptor's `|| readToken(REFRESH_TOKEN_KEY)` fallback would spend it —
    // minting an access token for somebody else and publishing it to the
    // same-origin session cookie. A session is its tokens; a missing token is
    // not a reason to keep the last user's.
    if (accessToken) writeToken(ACCESS_TOKEN_KEY, accessToken)
    else clearToken(ACCESS_TOKEN_KEY)
    if (refreshToken) writeToken(REFRESH_TOKEN_KEY, refreshToken)
    else clearToken(REFRESH_TOKEN_KEY)
  },

  // Merge fresh user fields (typically from GET /api/auth/me) into the
  // authenticated user object. Re-derives `name` and `email` so NavBar
  // and other consumers stay in sync without each having to know the
  // upstream response shape.
  MERGE_USER(state, fresh) {
    if (!fresh) return

    // A /me read may refresh your details; it MUST NOT change who you are.
    // The spread below replaces every field the payload carries — email,
    // user_id, is_admin, is_super_admin — so one /me made with a stale
    // privileged token used to rewrite the identity the navbar renders and
    // unlock the super-admin entries with it. Refuse a payload that belongs
    // to a different account (an undecidable comparison is allowed through:
    // see acceptMeIdentity).
    const verdict = acceptMeIdentity({
      sessionToken: state.accessToken,
      sessionUser: state.user,
      me: fresh
    })
    if (!verdict.ok) {
      console.warn('Ignoring a /me payload that is not this session:', verdict.reason)
      return
    }

    const merged = { ...(state.user || {}), ...fresh }
    merged.email = merged.email || fresh.username || fresh.sub || merged.username || merged.sub || null
    merged.name = merged.name
      || [fresh.first_name, fresh.last_name].filter(Boolean).join(' ')
      || [fresh.firstName, fresh.lastName].filter(Boolean).join(' ')
      || null
    state.user = merged
  },

  INCREMENT_LOGIN_ATTEMPTS(state) {
    state.loginAttempts++
    state.lastLoginAttempt = Date.now()
  },

  RESET_LOGIN_ATTEMPTS(state) {
    state.loginAttempts = 0
    state.lastLoginAttempt = null
  },

  LOGOUT(state) {
    state.isAuthenticated = false
    state.user = null
    state.accessToken = null
    state.refreshToken = null
    state.tokenValidSeconds = null

    // One call, the complete set: every session key from BOTH storages
    // (tokens, token_valid_seconds, the persisted vuex blob, the legacy
    // `authToken`/`token` orphans), the remember flag, and all three cookie
    // names the same-origin dashboard accepts — in every Domain variant,
    // because a host-only expiry leaves a parent-domain twin alive. Signing
    // out has to clear names this app never writes: the dashboard's own
    // sign-in bridge writes `access_token`, and a leftover one is exactly
    // what made two identities on one origin possible.
    clearSession()
  }
}

const actions = {
  async login({ commit, dispatch, getters, rootState }, credentials) {
    try {
      // Check rate limiting
      if (!getters.canAttemptLogin) {
        throw new Error('Too many login attempts. Please try again later.')
      }

      // NOTE: the remember-me flag is deliberately NOT set here, before the
      // request. It used to be, and a login that then FAILED left the flag
      // flipped while the previous session was still live — so that session's
      // next storage write (any mutation re-persists the auth module) moved
      // it into the other storage, leaving both storages populated and the
      // read order deciding which identity won. The flag is set below, after
      // the response, next to the clear and the write it governs.

      // Call auth service login
      const response = await authService.login(credentials)

      // Backend uses the unified standard response: { success, message, data, ... }
      // Tokens live at response.data.{access_token, refresh_token, ...}.
      // Tolerate the older flat shape (response.access_token) as a fallback so
      // an older deployed backend doesn't break the frontend.
      const payload = (response && response.success && response.data) ? response.data : response
      const accessToken = payload?.access_token || payload?.token

      if (accessToken) {
        // Normalize the user object so downstream components have a stable
        // shape. The login response's user is the JWT payload (username, sub,
        // user_id, ...) which doesn't expose `email` or `name` directly —
        // NavBar's `currentUser?.name || currentUser?.email` would fall
        // through to the literal string 'User' otherwise.
        const rawUser = payload.user || {}
        const normalizedUser = {
          ...rawUser,
          email: rawUser.email || rawUser.username || rawUser.sub || credentials.email || credentials.username,
          name: rawUser.name
            || [rawUser.first_name, rawUser.last_name].filter(Boolean).join(' ')
            || [rawUser.firstName, rawUser.lastName].filter(Boolean).join(' ')
            || null
        }

        const authData = {
          accessToken,
          refreshToken: payload.refresh_token,
          tokenValidSeconds: payload.expires_in || payload.token_valid_seconds,
          user: normalizedUser
        }

        // Signing in establishes a CLEAN session. Before the first new token
        // is written, wipe every artefact of whoever was signed in before —
        // both storages, the persisted vuex blob, and all three cookie names
        // the dashboard accepts. A login must never inherit anything from the
        // previous identity: a surviving refresh token is spendable, and a
        // surviving persisted blob is an identity the next page load adopts.
        clearSession()

        // Then the remember-me choice, which decides where writeToken() in
        // SET_AUTH_DATA puts the new tokens. Order matters: clearSession()
        // drops the flag with everything else.
        setRemember(!!credentials.rememberMe)

        commit('SET_AUTH_DATA', authData)
        commit('RESET_LOGIN_ATTEMPTS')

        // Close auth modal
        commit('setShowAuthModal', false, { root: true })

        dispatch('showNotification', {
          type: 'success',
          message: 'Authentication successful!'
        }, { root: true })

        // Marketplace Phase 2 (design spec §1.2) — consume the one-shot
        // post-auth redirect set when an anonymous user hit an auth-gated
        // flow (e.g. clicked Deploy). Push there, then clear it.
        const redirect = rootState?.postAuthRedirect
        if (redirect) {
          commit('setPostAuthRedirect', null, { root: true })
          // Defer so the modal teardown + token write settle first.
          // SET_AUTH_DATA above already wrote the token AND its same-origin
          // session cookie (token-storage.js), so a full browser load of a
          // platform-dashboard page (/status, /docs, ...) arrives signed in.
          // navigateAfterLogin() re-applies the open-redirect guard and picks
          // location.assign for paths the SPA router does not own.
          setTimeout(() => {
            navigateAfterLogin(redirect, { router })
          }, 0)
        }

        return { success: true }
      } else {
        throw new Error(response.message || 'Authentication failed')
      }
    } catch (error) {
      commit('INCREMENT_LOGIN_ATTEMPTS')

      // Extract error message from API response
      let errorMessage = 'Authentication failed'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.status === false && error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      dispatch('showNotification', {
        type: 'error',
        message: errorMessage
      }, { root: true })

      // Throw the error so the component can handle it
      throw new Error(errorMessage)
    }
  },

  async logout({ commit, dispatch }) {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout API call failed:', error)
    }

    // Update state
    commit('LOGOUT')

    dispatch('showNotification', {
      type: 'info',
      message: 'Logged out successfully'
    }, { root: true })
    
    // Redirect to home
    if (router.currentRoute.value.meta.requiresAuth) {
      router.push('/')
    }
  },
  
  async refreshToken({ state, commit, dispatch }) {
    try {
      if (!state.refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await authService.refreshToken(state.refreshToken)

      // `authService.refreshToken` returns the WHOLE unified envelope
      // ({ success, message, data: { access_token, ... } }), so reading
      // `response.access_token` was always undefined against the deployed
      // auth-api — this action committed a token-less but still
      // `isAuthenticated: true` session, and (with the old `if (accessToken)`
      // guard in SET_AUTH_DATA) left the previous tokens in storage for the
      // request interceptors to keep using. The 401 interceptor in
      // auth-api.js has always read the envelope correctly; this is the same
      // reading.
      const data = response?.data && typeof response.data === 'object' ? response.data : response
      const accessToken = data?.access_token || data?.token
      if (!accessToken) {
        throw new Error('Refresh response carried no access token')
      }

      // A refresh renews the CURRENT session; it never becomes somebody
      // else's. On a mismatch the catch below signs out, which is the honest
      // outcome: the stored refresh token was not this session's.
      const verdict = acceptRefreshedToken({
        sessionToken: state.accessToken,
        sessionUser: state.user,
        newToken: accessToken
      })
      if (!verdict.ok) {
        throw new Error(`Refused a refreshed token: ${verdict.reason}`)
      }

      const authData = {
        accessToken,
        refreshToken: data.refresh_token || state.refreshToken,
        tokenValidSeconds: data.expires_in || data.token_valid_seconds,
        user: state.user
      }

      commit('SET_AUTH_DATA', authData)

      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      dispatch('logout')
      return false
    }
  },

  async updateTokens({ commit, state }, { accessToken, refreshToken, tokenValidSeconds }) {
    const authData = {
      accessToken,
      refreshToken,
      tokenValidSeconds,
      user: state.user
    }
    commit('SET_AUTH_DATA', authData)
  },

  // Phase G v1.1 Sprint 2 — switch the current organization and re-issue
  // the JWT. Used by the BuyPlan wizard after the new workspace org is
  // activated so subsequent API calls carry `current_organization_id`.
  async switchOrganization({ state, commit, dispatch }, organizationId) {
    try {
      const data = await authService.switchOrganization(organizationId)
      const accessToken = data?.access_token || data?.token
      if (!accessToken) {
        throw new Error('Switch-organization response missing access token')
      }

      commit('SET_AUTH_DATA', {
        accessToken,
        refreshToken: data.refresh_token || state.refreshToken,
        tokenValidSeconds: data.expires_in || data.token_valid_seconds || state.tokenValidSeconds,
        // Preserve the existing normalized user object — fetchMe below
        // will refresh it with the new org context.
        user: state.user
      })

      // Refresh /me so consumers (NavBar, Dashboard) see the new active
      // org's tier/allocation/role without waiting on a route change.
      try {
        await dispatch('fetchMe')
      } catch (e) {
        console.warn('fetchMe after switchOrganization failed:', e?.message || e)
      }

      return { success: true, data }
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to switch organization'
      }, { root: true })
      throw error
    }
  },

  // Pull the latest /me payload into the auth store so any component
  // (NavBar, Dashboard) reads the same source of truth — including the
  // active org, tier, allocation, and the full org list for the
  // switcher. Safe to call repeatedly; idempotent.
  async fetchMe({ commit, state, dispatch }) {
    if (!state.isAuthenticated) return null
    try {
      const me = await authService.getMe()
      // Personal lock (tenant lifecycle `account_locked`) is the existing
      // ban path: auth-api refuses the login and the next token use. A /me
      // that still answers for a locked account must not leave a working
      // session behind, so sign out with the same plain sentence. An org
      // lock is different — it never signs anyone out (see org-lifecycle.js).
      if (me && me.account_locked === true) {
        await dispatch('logout')
        dispatch('showNotification', {
          type: 'error',
          message: 'Your account is locked. Contact support if you think this is a mistake.'
        }, { root: true })
        return null
      }
      commit('MERGE_USER', me)
      return me
    } catch (error) {
      console.warn('fetchMe failed:', error?.message || error)
      return null
    }
  },

  // Pin an org as the user's default. The current session keeps its
  // active org; the pin only takes effect on next login. We optimistically
  // mark the chosen org as `is_default` in the cached /me payload so the
  // UI updates without a round-trip to /me.
  async setDefaultOrganization({ dispatch, state, commit }, organizationId) {
    try {
      await authService.setDefaultOrganization(organizationId)
      // Cheap local update: flip is_default flags on the cached org list
      // so the dropdown re-renders without waiting on /me. fetchMe is
      // also kicked off as a safety net.
      const orgs = state.user?.organizations
      if (Array.isArray(orgs)) {
        const merged = {
          ...(state.user || {}),
          organizations: orgs.map((o) => ({
            ...o,
            is_default: o.id === organizationId,
          })),
        }
        commit('MERGE_USER', merged)
      }
      dispatch('fetchMe').catch(() => {})
      return { success: true }
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to set default organization'
      }, { root: true })
      throw error
    }
  },
  
  /**
   * App start, BEFORE the session cookie is re-asserted and before
   * initializeAuth: check that the session vuex-persist just rehydrated is
   * the same session as the token on disk. If they disagree the origin is
   * holding two sessions and neither can be trusted — wipe and render signed
   * out rather than letting the read order pick a winner (which is how a
   * stale, more privileged session could outrank the live one).
   */
  reconcileSession({ state, commit }) {
    const verdict = bootSessionVerdict({
      storedToken: readToken(ACCESS_TOKEN_KEY),
      stateToken: state.accessToken,
      stateUser: state.user
    })
    if (verdict === 'clear') {
      console.warn('Session storage held two different sessions — signing out.')
      commit('LOGOUT')
      return false
    }
    return true
  },

  async initializeAuth({ state, dispatch }) {
    // Check if we have tokens in state (from vuex-persist)
    if (state.accessToken && state.refreshToken) {
      try {
        // Try to decode the token to check expiry
        const tokenParts = state.accessToken.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          const expiryTime = payload.exp * 1000
          const now = Date.now()

          if (expiryTime > now) {
            // Token is still valid, no need to schedule refresh - interceptor will handle it
            return true
          }
        }

        // Token expired, try refresh
        return await dispatch('refreshToken')
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        dispatch('logout')
        return false
      }
    }
    return false
  },
  
  async updateUserProfile({ state, commit, dispatch }, profileData) {
    try {
      console.log('Updating profile with data:', profileData)
      console.log('Auth state has token:', !!state.accessToken)
      const response = await authService.updateUserProfile(profileData)
      
      if (response.success !== false) {
        // Update user data in state
        const updatedUser = {
          ...state.user,
          ...profileData
        }
        commit('SET_AUTH_DATA', {
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          tokenValidSeconds: state.tokenValidSeconds,
          user: updatedUser
        })
        
        dispatch('showNotification', {
          type: 'success',
          message: 'Profile updated successfully!'
        }, { root: true })
        
        return { success: true, data: response }
      } else {
        throw new Error(response.message || 'Profile update failed')
      }
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to update profile'
      }, { root: true })
      
      throw error
    }
  },
  
  async updatePassword({ dispatch }, passwordData) {
    try {
      // No username is sent: auth-api's POST /api/auth/password/change resolves
      // the account from the bearer token's user_id.
      const response = await authService.changePassword(passwordData)

      if (response.success !== false) {
        dispatch('showNotification', {
          type: 'success',
          message: 'Password updated successfully!'
        }, { root: true })
        
        return { success: true }
      } else {
        throw new Error(response.message || 'Password update failed')
      }
    } catch (error) {
      // Surface the server's own reason (wrong current password, password too
      // weak, ...) rather than a generic failure line. See
      // src/utils/password-change.js for why `details` is preferred.
      dispatch('showNotification', {
        type: 'error',
        message: changePasswordErrorMessage(error)
      }, { root: true })

      throw error
    }
  },
  
  async fetchUserProfile({ state, commit, dispatch }) {
    try {
      const username = state.user?.email || state.user?.username
      if (!username) {
        throw new Error('No username available for profile fetch')
      }
      
      const response = await authService.getUserProfile(username)
      
      if (response.success !== false && response.data) {
        // Update user data with profile information
        const updatedUser = {
          ...state.user,
          firstName: response.data.first_name,
          lastName: response.data.last_name,
          email: response.data.username,
          phone: response.data.phone,
          birthDate: response.data.birthd,
          userType: response.data.user_type,
          regDate: response.data.reg_date,
          lastLogin: response.data.last_login,
          status: response.data.status
        }
        
        commit('SET_AUTH_DATA', {
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          tokenValidSeconds: state.tokenValidSeconds,
          user: updatedUser
        })
        
        return { success: true, data: response.data }
      } else {
        throw new Error(response.message || 'Failed to fetch user profile')
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      dispatch('showNotification', {
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to load profile data'
      }, { root: true })
      
      throw error
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
